import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { labelsColors, Rect, RectType } from "../../models/Rect";
import { Dialog } from "../dialog/Dialog";
import { ImageCanvas } from "./ImageCanvas";
import { Box, FormControlLabel, Grid, Switch, TextField, Typography } from "@mui/material";
import { translationService } from "../../services/translation/TranslationService";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import dateService from "../../services/date-service/DateService";
import LabelingInfo from "./LabelingInfo";

type Props = {
  isVisible: boolean;
  rectTypes: RectType[];
  onClose: () => void;
  onComplete: (setMainInfo: boolean) => void;
  screenshot: string | null;
  rects: Map<RectType, Rect>;
  setRects: (rects: Map<RectType, Rect>) => void;
};

export const LabelingDialog: React.FC<Props> = observer(
  ({ isVisible, rectTypes, onClose, onComplete, screenshot, rects, setRects }) => {
    const [rectsInput, setRectsInput] = useState<Map<RectType, string>>(new Map());
    const [rectType, setRectType] = useState<RectType | null>(null);
    const [detectMainInfo, setDetectMainInfo] = useState(true);
    const detectionTime = dateService.formatTime(videoPlayerService.lastScreenshotTimestamp);
    const endTime = dateService.formatTime(videoPlayerService.videoDuration);

    const handleTextChange = (key: RectType, value: string) => {
      setRectsInput((prev) => {
        const rectsInputCopy = new Map(prev);
        rectsInputCopy.set(key, value);
        return rectsInputCopy;
      });
      const parts = value.split(",").map((v) => parseFloat(v.trim()));
      if (parts.length === 4 && parts.every((v) => !isNaN(v))) {
        const [x, y, w, h] = parts.map((v) => Math.round(v));
        let rectsCopy = rects;
        rectsCopy.set(key, { x, y, w, h, selected: true, visible: true });
      }
    };
    useEffect(() => {
      Array.from(rects.keys()).forEach((key) => {
        const x = Math.round(rects.get(key)!.x);
        const y = Math.round(rects.get(key)!.y);
        const w = Math.round(rects.get(key)!.w);
        const h = Math.round(rects.get(key)!.h);
        let rectsInputCopy = new Map(rectsInput);
        rectsInputCopy.set(key, `${x},${y},${w},${h}`);
        setRectsInput(rectsInputCopy);
      });
    }, [rects]);

    useEffect(() => {
      setRectType(rectTypes[0]);
    }, [rectTypes]);

    if (rectTypes.length === 0 || !screenshot) return null;

    return (
      <Dialog
        titleKey={`ocrService.dialogTitle.${rectTypes[0]}`}
        isVisible={isVisible}
        subTextKey={`ocrService.dialogSubText.${rectTypes[0]}`}
        onHide={onClose}
        buttonOk={{
          textKey: "general.ok",
          action: () => onComplete(detectMainInfo),
        }}
      >
        <Grid container xs={12} spacing={2}>
          <Grid item xs={12}>
            {rectTypes[0] === "main" && (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={detectMainInfo}
                      onChange={(e) => setDetectMainInfo(e.target.checked)}
                    />
                  }
                  label={translationService.translate(
                    `ocrService.dialogContent.${rectTypes[0]}.toggle`
                  )}
                  sx={{ mr: 4 }}
                />
              </>
            )}
          </Grid>
          {rectTypes[0] === "detections" ? (
            <>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  size="small"
                  value={detectionTime}
                  InputProps={{ readOnly: true }}
                    label={translationService.translate(`ocrService.dialogContent.${rectTypes[0]}.time`)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  size="small"
                  value={endTime}
                  InputProps={{ readOnly: true }}
                    label={translationService.translate(`ocrService.dialogContent.${rectTypes[0]}.endtime`)}
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                size="small"
                value={detectionTime}
                InputProps={{ readOnly: true }}
                  label={translationService.translate(`ocrService.dialogContent.${rectTypes[0]}.time`)}
              />
            </Grid>
          )}
          {rectTypes.map((rectTypeItem) => (
            <>
              <Grid item xs={2}>
                  <Box component="button" onClick={() => setRectType(rectTypeItem)}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: rectType == rectTypeItem ? labelsColors[rectTypeItem] : "grey.400",
                    border: "none",
                    borderRadius: 1,
                    cursor: "pointer",
                    color: "common.white",
                  }}
                />
              </Grid>
              <Grid item xs={9}>
                <TextField
                  value={rectsInput.get(rectTypeItem)}
                  onChange={(e) => handleTextChange(rectTypeItem, e.target.value)}
                  variant="outlined"
                  size="small"
                  sx={{ flex: 1 }}
                    label={translationService.translate(`ocrService.dialogContent.${rectTypeItem}.coords`)}
                    InputLabelProps={{shrink: true , style: {color: labelsColors[rectTypeItem]}}}
                />
              </Grid>
            </>
          ))}
          <Grid item xs={1}>
            <LabelingInfo type={rectTypes[0]} />
          </Grid>
          <Grid item xs={12}>
            <ImageCanvas
              rects={rects}
              rectType={rectType}
              setRects={setRects}
              imageSrc={screenshot}
              disabled={!detectMainInfo}
            />
          </Grid>
        </Grid>
      </Dialog>
    );
  }
);
